import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

const EMPLOYMENT_PAY = {
  FULL_TIME: { weekday_rate: 36.0, saturday_rate: 38.09, superannuation_rate: 11.5 },
  PART_TIME: { weekday_rate: 36.0, saturday_rate: 38.09, superannuation_rate: 11.5 },
  CASUAL: { weekday_rate: 40.0, saturday_rate: 46.55, superannuation_rate: 11.5 },
} as const;

const pageCardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
  marginBottom: 'var(--spacing-1)',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-accent)',
  border: 'none',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text-inverse)',
  fontWeight: 600,
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontWeight: 500,
};

const dangerButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'rgba(220, 53, 69, 0.12)',
  border: '1px solid rgba(220, 53, 69, 0.25)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-error)',
  fontWeight: 600,
};

function BarberForm({
  barber,
  locations,
  allLocations,
  onCancel,
  onSave,
  saving,
}: {
  barber?: any;
  /** Trading shops — the only ones a barber can be newly assigned to. */
  locations: any[];
  /** Including closed shops, so a stale assignment can be removed. */
  allLocations: any[];
  onCancel: () => void;
  onSave: (data: {
    name: string;
    location_ids: number[];
    employment_type: 'FULL_TIME' | 'PART_TIME' | 'CASUAL';
    weekday_rate: number;
    saturday_rate: number;
    superannuation_rate: number;
  }) => void;
  saving: boolean;
}) {
  const [name, setName] = useState(barber?.name || '');
  // Multi-select location ids (Set<number>). Pre-populate from the
  // barber's `locations[]` returned by the API (post-migration 047
  // shape); for new barbers start with the first location selected.
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<number>>(() => {
    const initial = new Set<number>();
    const fromApi: Array<{ id: number }> | undefined = barber?.locations;
    if (Array.isArray(fromApi) && fromApi.length > 0) {
      for (const l of fromApi) initial.add(Number(l.id));
    } else if (barber?.location_id) {
      initial.add(Number(barber.location_id));
    } else if (locations[0]?.id) {
      initial.add(Number(locations[0].id));
    }
    return initial;
  });
  const toggleLocation = (id: number) => {
    setSelectedLocationIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // Trading shops, plus any closed shop this barber is STILL attached to.
  // Without the second part a stale assignment is unremovable: the closed
  // shop has no checkbox, so there is no way to untick it. Lee and Loren
  // moved to Mitchelton but kept Nundah, and the form could not let go of
  // it. A closed shop only appears while it is ticked, so it can be
  // removed but never newly added.
  const selectableLocations = useMemo(() => {
    const enabledIds = new Set(locations.map((l: any) => Number(l.id)));
    const stale = allLocations
      .filter((l: any) => !enabledIds.has(Number(l.id)))
      .filter((l: any) => selectedLocationIds.has(Number(l.id)))
      .map((l: any) => ({ ...l, closed: true }));
    return [...locations.map((l: any) => ({ ...l, closed: false })), ...stale];
  }, [locations, allLocations, selectedLocationIds]);

  const [employmentType, setEmploymentType] = useState<'FULL_TIME' | 'PART_TIME' | 'CASUAL'>(
    barber?.employment_type || 'FULL_TIME',
  );
  const payBand = EMPLOYMENT_PAY[employmentType];

  useEffect(() => {
    // Seed the default selection for a NEW barber when the locations
    // list arrives async after the form mounts.
    if (!barber && selectedLocationIds.size === 0 && locations[0]?.id) {
      setSelectedLocationIds(new Set([Number(locations[0].id)]));
    }
  }, [barber, selectedLocationIds, locations]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || selectedLocationIds.size === 0) return;
    onSave({
      name: name.trim(),
      location_ids: Array.from(selectedLocationIds).sort((a, b) => a - b),
      employment_type: employmentType,
      weekday_rate: payBand.weekday_rate,
      saturday_rate: payBand.saturday_rate,
      superannuation_rate: payBand.superannuation_rate,
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{ ...pageCardStyle, marginBottom: 'var(--spacing-4)' }}>
      <div
        style={{
          display: 'grid',
          gap: 'var(--spacing-3)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        }}
      >
        <div>
          <label style={labelStyle}>Barber Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Lee"
            required
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Locations</label>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--spacing-2)',
              padding: '6px 10px',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-default)',
              backgroundColor: 'var(--color-background)',
              minHeight: '38px',
              alignItems: 'center',
            }}
          >
            {selectableLocations.map((location) => {
              const checked = selectedLocationIds.has(Number(location.id));
              return (
                <label
                  key={location.id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: '13px',
                    cursor: 'pointer',
                    padding: '2px 8px',
                    borderRadius: '999px',
                    whiteSpace: 'nowrap',
                    backgroundColor: checked ? 'rgba(217,168,67,0.18)' : 'transparent',
                    border: checked
                      ? '1px solid var(--color-primary, #d9a843)'
                      : '1px solid var(--color-border)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleLocation(Number(location.id))}
                    style={{ margin: 0 }}
                  />
                  {location.name}
                  {location.closed ? (
                    <span style={{ color: 'var(--color-text-muted)', fontSize: '11px' }}>
                      · closed
                    </span>
                  ) : null}
                </label>
              );
            })}
          </div>
          {selectableLocations.some((l: any) => l.closed) ? (
            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: 4 }}>
              This barber is still attached to a shop that has closed. Untick it to remove them.
            </p>
          ) : null}
          {selectedLocationIds.size === 0 ? (
            <p style={{ fontSize: '12px', color: 'var(--color-error, #dc3545)', marginTop: 4 }}>
              Pick at least one location.
            </p>
          ) : null}
        </div>
        <div>
          <label style={labelStyle}>Employment</label>
          <select
            value={employmentType}
            onChange={(e) => setEmploymentType(e.target.value as any)}
            style={inputStyle}
          >
            <option value="FULL_TIME">Full Time</option>
            <option value="PART_TIME">Part Time</option>
            <option value="CASUAL">Casual</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Mon-Fri Rate</label>
          <input
            value={`$${payBand.weekday_rate.toFixed(2)}`}
            readOnly
            style={{ ...inputStyle, backgroundColor: 'var(--color-surface-elevated)' }}
          />
        </div>
        <div>
          <label style={labelStyle}>Saturday Rate</label>
          <input
            value={`$${payBand.saturday_rate.toFixed(2)}`}
            readOnly
            style={{ ...inputStyle, backgroundColor: 'var(--color-surface-elevated)' }}
          />
        </div>
        <div>
          <label style={labelStyle}>Super</label>
          <input
            value={`${payBand.superannuation_rate.toFixed(1)}%`}
            readOnly
            style={{ ...inputStyle, backgroundColor: 'var(--color-surface-elevated)' }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-4)' }}>
        <button
          type="submit"
          disabled={saving}
          style={{ ...primaryButtonStyle, opacity: saving ? 0.7 : 1 }}
        >
          {saving ? 'Saving...' : barber ? 'Update Barber' : 'Add Barber'}
        </button>
        <button type="button" onClick={onCancel} style={secondaryButtonStyle}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function Barbers() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingBarber, setEditingBarber] = useState<any | null>(null);
  const [selectedBarberId, setSelectedBarberId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [syncResult, setSyncResult] = useState<any | null>(null);
  const [pendingDeactivate, setPendingDeactivate] = useState<any | null>(null);
  // Impact returned from the deactivation call — shown as a persistent
  // alert after Confirm so the admin has the list of bookings still on
  // the calendar to handle (reassign / cancel manually).
  const [postDeactivationImpact, setPostDeactivationImpact] = useState<any | null>(null);
  // When ON, the Barbers list also shows deactivated staff so an admin
  // can reactivate someone who was disabled by mistake or is returning.
  const [showInactive, setShowInactive] = useState(false);

  const { data: barbers = [], isLoading: loadingBarbers } = useQuery({
    queryKey: ['barbers', showInactive],
    queryFn: () => api.getBarbers(undefined, { includeInactive: showInactive }),
  });
  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: api.getLocations,
  });

  // Closed shops too — a barber left attached to one needs a checkbox to
  // untick, and `getLocations` only returns the trading ones.
  const { data: allLocations = [] } = useQuery({
    queryKey: ['locations', 'all'],
    queryFn: api.getAllLocations,
  });
  const { data: services = [] } = useQuery({
    queryKey: ['services'],
    queryFn: api.getServices,
  });
  const { data: assignedServices = [] } = useQuery({
    queryKey: ['barber-services', selectedBarberId],
    queryFn: () => api.getBarberServices(selectedBarberId as number),
    enabled: !!selectedBarberId,
  });

  useEffect(() => {
    if (!selectedBarberId && barbers[0]?.id) {
      setSelectedBarberId(barbers[0].id);
    }
  }, [barbers, selectedBarberId]);

  const createMutation = useMutation({
    mutationFn: api.createBarber,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['barbers'] });
      setShowForm(false);
      setSelectedBarberId(created.id);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => api.updateBarber(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['barbers'] });
      setEditingBarber(null);
    },
  });

  // Deactivation itself lives in DeactivationDialog — it fetches an
  // impact preview, calls DELETE /barbers/:id, and bubbles the
  // resulting impact via onConfirmed (below at pendingDeactivate JSX).

  const syncMutation = useMutation({
    mutationFn: api.syncBarbersWithSquare,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['barbers'] });
      setSyncResult(data);
      setTimeout(() => setSyncResult(null), 8000);
    },
  });

  const assignMutation = useMutation({
    mutationFn: ({ barberId, serviceId }: { barberId: number; serviceId: number }) =>
      api.assignBarberService(barberId, serviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['barber-services', selectedBarberId] });
    },
  });

  const removeAssignMutation = useMutation({
    mutationFn: ({ barberId, serviceId }: { barberId: number; serviceId: number }) =>
      api.removeBarberService(barberId, serviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['barber-services', selectedBarberId] });
    },
  });

  const locationNameById = useMemo(
    () => new Map(locations.map((location: any) => [location.id, location.name])),
    [locations],
  );

  const selectedBarber = barbers.find((barber: any) => barber.id === selectedBarberId) || null;
  const assignedServiceIds = new Set(assignedServices.map((service: any) => service.id));
  const filteredBarbers = barbers.filter((barber: any) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return (
      barber.name.toLowerCase().includes(term) ||
      String(locationNameById.get(barber.location_id) || '')
        .toLowerCase()
        .includes(term)
    );
  });

  return (
    <>
      <div className="barbers-page">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--spacing-4)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <div>
            <h1>Barbers</h1>
            <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-2)' }}>
              Add and edit barbers, manage their location, and control which services they can
              perform.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <button
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
              style={{
                ...secondaryButtonStyle,
                borderColor: 'var(--color-accent)',
                color: 'var(--color-accent)',
              }}
            >
              {syncMutation.isPending ? 'Syncing...' : '⟳ Sync with Square'}
            </button>
            <button
              onClick={() => {
                setEditingBarber(null);
                setShowForm(true);
              }}
              style={primaryButtonStyle}
            >
              + Add Barber
            </button>
          </div>
        </div>

        {syncResult && (
          <div
            style={{
              ...pageCardStyle,
              marginBottom: 'var(--spacing-4)',
              backgroundColor: 'rgba(217, 178, 86, 0.08)',
              borderColor: 'rgba(217, 178, 86, 0.28)',
            }}
          >
            <strong>Sync complete.</strong> Created: {syncResult.created || 0}, updated:{' '}
            {syncResult.updated || 0}, skipped: {syncResult.skipped || 0}.
          </div>
        )}

        {(showForm || editingBarber) && (
          <BarberForm
            barber={editingBarber || undefined}
            locations={locations}
            allLocations={allLocations}
            saving={createMutation.isPending || updateMutation.isPending}
            onCancel={() => {
              setShowForm(false);
              setEditingBarber(null);
            }}
            onSave={(data) => {
              if (editingBarber) {
                updateMutation.mutate({ id: editingBarber.id, data });
              } else {
                createMutation.mutate(data);
              }
            }}
          />
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 'var(--spacing-4)',
          }}
        >
          <section style={pageCardStyle}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 'var(--spacing-3)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              <h2 style={{ margin: 0 }}>Team</h2>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search barbers..."
                style={{ ...inputStyle, width: '220px' }}
              />
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-2)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-muted)',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                Show inactive
              </label>
            </div>

            {loadingBarbers ? (
              <p style={{ color: 'var(--color-text-muted)' }}>Loading barbers...</p>
            ) : filteredBarbers.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)' }}>No barbers found.</p>
            ) : (
              <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
                {filteredBarbers.map((barber: any) => {
                  const isSelected = barber.id === selectedBarberId;
                  return (
                    <button
                      key={barber.id}
                      onClick={() => setSelectedBarberId(barber.id)}
                      style={{
                        cursor: 'pointer',
                        textAlign: 'left',
                        ...pageCardStyle,
                        padding: 'var(--spacing-3)',
                        borderColor: isSelected ? 'var(--color-accent)' : 'var(--color-border)',
                        backgroundColor: isSelected
                          ? 'rgba(217, 178, 86, 0.08)'
                          : 'var(--color-surface)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 'var(--spacing-3)',
                        }}
                      >
                        <div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--spacing-2)',
                            }}
                          >
                            <strong>{barber.name}</strong>
                            <span
                              title={
                                barber.square_team_member_id
                                  ? 'Mapped to Square team member'
                                  : 'Not mapped to Square'
                              }
                              style={{
                                color: barber.square_team_member_id
                                  ? 'var(--color-success)'
                                  : 'var(--color-text-muted)',
                                fontSize: '14px',
                              }}
                            >
                              {barber.square_team_member_id ? '✓' : '○'}
                            </span>
                          </div>
                          <p
                            style={{
                              color: 'var(--color-text-muted)',
                              marginTop: 'var(--spacing-1)',
                            }}
                          >
                            {/* Prefer the API's locations[] array — for a multi-location
                                barber (Kiana → Nundah + Mitchelton) we render every name
                                joined with " + ". Falls back to the id lookup for legacy
                                single-location callers. */}
                            {Array.isArray((barber as any).locations) &&
                            (barber as any).locations.length > 0
                              ? (barber as any).locations.map((l: any) => l.name).join(' + ')
                              : locationNameById.get(barber.location_id) || 'Unknown location'}
                          </p>
                        </div>
                        <span
                          style={{
                            padding: '4px 10px',
                            borderRadius: '999px',
                            backgroundColor: barber.square_team_member_id
                              ? 'rgba(40, 167, 69, 0.12)'
                              : 'var(--color-surface-elevated)',
                            color: barber.square_team_member_id
                              ? 'var(--color-success)'
                              : 'var(--color-text-muted)',
                            fontSize: '12px',
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                          }}
                        >
                          {barber.square_team_member_id ? 'Square linked' : 'Local only'}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section style={pageCardStyle}>
            {!selectedBarber ? (
              <p style={{ color: 'var(--color-text-muted)' }}>
                Select a barber to manage services.
              </p>
            ) : (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 'var(--spacing-3)',
                    marginBottom: 'var(--spacing-4)',
                  }}
                >
                  <div>
                    <h2 style={{ margin: 0 }}>{selectedBarber.name}</h2>
                    <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-1)' }}>
                      {Array.isArray((selectedBarber as any).locations) &&
                      (selectedBarber as any).locations.length > 0
                        ? (selectedBarber as any).locations.map((l: any) => l.name).join(' + ')
                        : locationNameById.get(selectedBarber.location_id) || 'Unknown location'}
                    </p>
                    <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-1)' }}>
                      {selectedBarber.employment_type
                        ? selectedBarber.employment_type.replace('_', ' ')
                        : 'Employment not set'}
                    </p>
                    <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-1)' }}>
                      Mon-Fri ${Number(selectedBarber.weekday_rate || 0).toFixed(2)} · Sat $
                      {Number(selectedBarber.saturday_rate || 0).toFixed(2)} · Super{' '}
                      {Number(selectedBarber.superannuation_rate || 0).toFixed(1)}%
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                    <button
                      onClick={() => setEditingBarber(selectedBarber)}
                      style={secondaryButtonStyle}
                    >
                      Edit
                    </button>
                    {selectedBarber.active === false ? (
                      <button
                        onClick={() =>
                          updateMutation.mutate({
                            id: selectedBarber.id,
                            data: { active: true },
                          })
                        }
                        disabled={updateMutation.isPending}
                        style={{
                          ...primaryButtonStyle,
                          opacity: updateMutation.isPending ? 0.6 : 1,
                          cursor: updateMutation.isPending ? 'wait' : 'pointer',
                        }}
                      >
                        {updateMutation.isPending ? 'Reactivating…' : 'Reactivate'}
                      </button>
                    ) : (
                      <button
                        onClick={() => setPendingDeactivate(selectedBarber)}
                        style={dangerButtonStyle}
                      >
                        Deactivate
                      </button>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: 'var(--spacing-4)' }}>
                  <h3 style={{ marginBottom: 'var(--spacing-2)' }}>Assigned Services</h3>
                  {assignedServices.length === 0 ? (
                    <p style={{ color: 'var(--color-text-muted)' }}>No services assigned yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
                      {assignedServices.map((service: any) => (
                        <button
                          key={service.id}
                          onClick={() =>
                            removeAssignMutation.mutate({
                              barberId: selectedBarber.id,
                              serviceId: service.id,
                            })
                          }
                          style={{
                            ...secondaryButtonStyle,
                            padding: '6px 12px',
                            backgroundColor: 'rgba(217, 178, 86, 0.12)',
                            borderColor: 'rgba(217, 178, 86, 0.3)',
                          }}
                          title="Remove service assignment"
                        >
                          {service.name} ×
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h3 style={{ marginBottom: 'var(--spacing-2)' }}>Available Services</h3>
                  <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                    {services.map((service: any) => {
                      const assigned = assignedServiceIds.has(service.id);
                      return (
                        <div
                          key={service.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 'var(--spacing-3)',
                            padding: 'var(--spacing-3)',
                            backgroundColor: assigned
                              ? 'rgba(217, 178, 86, 0.08)'
                              : 'var(--color-surface-elevated)',
                            border: `1px solid ${assigned ? 'rgba(217, 178, 86, 0.3)' : 'var(--color-border)'}`,
                            borderRadius: 'var(--radius-default)',
                          }}
                        >
                          <div>
                            <strong>{service.name}</strong>
                            <p
                              style={{
                                color: 'var(--color-text-muted)',
                                marginTop: 'var(--spacing-1)',
                              }}
                            >
                              {service.duration_minutes} mins · ${service.price}
                            </p>
                          </div>
                          {assigned ? (
                            <button
                              onClick={() =>
                                removeAssignMutation.mutate({
                                  barberId: selectedBarber.id,
                                  serviceId: service.id,
                                })
                              }
                              style={secondaryButtonStyle}
                            >
                              Remove
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                assignMutation.mutate({
                                  barberId: selectedBarber.id,
                                  serviceId: service.id,
                                })
                              }
                              style={primaryButtonStyle}
                            >
                              Assign
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>

      <TeamOrderPerLocation locations={locations} />

      {pendingDeactivate && (
        <DeactivationDialog
          barber={pendingDeactivate}
          onCancel={() => setPendingDeactivate(null)}
          onConfirmed={(impact) => {
            setPendingDeactivate(null);
            setSelectedBarberId(null);
            if (impact.affectedBookings.length > 0) setPostDeactivationImpact(impact);
          }}
        />
      )}
      {postDeactivationImpact && (
        <PostDeactivationAlert
          impact={postDeactivationImpact}
          onClose={() => setPostDeactivationImpact(null)}
        />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// Team order per location
// ────────────────────────────────────────────────────────────────────
//
// Same barber can occupy different positions at different locations
// (Nicola near the top at Nundah, further down at Mitchelton). The
// selected location is queried on its own so `getBarbers({locationId})`
// returns rows already sorted by `barber_location.display_order` — the
// arrow buttons then post the new top-to-bottom order back and let
// React Query refresh every consumer.

function TeamOrderPerLocation({ locations }: { locations: any[] }) {
  const queryClient = useQueryClient();
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

  useEffect(() => {
    if (selectedLocationId === null && locations[0]?.id) {
      setSelectedLocationId(locations[0].id);
    }
  }, [locations, selectedLocationId]);

  const { data: locationBarbers = [], isLoading } = useQuery({
    queryKey: ['barbers', 'ordered', selectedLocationId],
    queryFn: () => api.getBarbers(selectedLocationId as number),
    enabled: !!selectedLocationId,
  });

  const reorderMutation = useMutation({
    mutationFn: ({ locationId, ids }: { locationId: number; ids: number[] }) =>
      api.setBarberOrderForLocation(locationId, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['barbers'] });
    },
  });

  const move = (fromIndex: number, direction: -1 | 1) => {
    if (!selectedLocationId) return;
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= locationBarbers.length) return;
    const ids = locationBarbers.map((b: any) => b.id);
    [ids[fromIndex], ids[toIndex]] = [ids[toIndex], ids[fromIndex]];
    reorderMutation.mutate({ locationId: selectedLocationId, ids });
  };

  return (
    <div style={{ ...pageCardStyle, marginTop: 'var(--spacing-4)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--spacing-3)',
          gap: 'var(--spacing-3)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>Team order per location</h2>
          <p
            style={{
              color: 'var(--color-text-muted)',
              fontSize: 'var(--font-size-sm)',
              margin: '4px 0 0 0',
            }}
          >
            Sets the order barbers appear in on the Calendar and Rosters at this location.
          </p>
        </div>
        <select
          value={selectedLocationId ?? ''}
          onChange={(e) => setSelectedLocationId(Number(e.target.value))}
          style={{ ...inputStyle, width: '220px' }}
        >
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p>
      ) : locationBarbers.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>No barbers at this location.</p>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
          {locationBarbers.map((barber: any, i: number) => (
            <div
              key={barber.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-default)',
                backgroundColor: 'var(--color-surface-elevated)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
                <span
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    color: 'var(--color-text-muted)',
                    minWidth: '1.5em',
                  }}
                >
                  {i + 1}.
                </span>
                <strong>{barber.name}</strong>
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                <button
                  onClick={() => move(i, -1)}
                  disabled={i === 0 || reorderMutation.isPending}
                  style={{
                    ...secondaryButtonStyle,
                    padding: '4px 10px',
                    opacity: i === 0 || reorderMutation.isPending ? 0.4 : 1,
                    cursor: i === 0 || reorderMutation.isPending ? 'not-allowed' : 'pointer',
                  }}
                  aria-label={`Move ${barber.name} up`}
                >
                  ↑
                </button>
                <button
                  onClick={() => move(i, 1)}
                  disabled={i === locationBarbers.length - 1 || reorderMutation.isPending}
                  style={{
                    ...secondaryButtonStyle,
                    padding: '4px 10px',
                    opacity:
                      i === locationBarbers.length - 1 || reorderMutation.isPending ? 0.4 : 1,
                    cursor:
                      i === locationBarbers.length - 1 || reorderMutation.isPending
                        ? 'not-allowed'
                        : 'pointer',
                  }}
                  aria-label={`Move ${barber.name} down`}
                >
                  ↓
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Deactivation flow
// ────────────────────────────────────────────────────────────────────

interface ImpactBooking {
  bookingId: string;
  brisbaneDate: string;
  brisbaneTime: string;
  brisbaneDay: string;
  customerName: string | null;
  customerPhone: string | null;
  locationName: string;
  serviceNames: string[];
  status: string;
}

interface Impact {
  barberId: number;
  barberName: string;
  shiftsToRemove: number;
  breaksToRemove: number;
  affectedBookings: ImpactBooking[];
}

function BookingRow({ b }: { b: ImpactBooking }) {
  return (
    <li style={{ marginBottom: 6 }}>
      <strong>{b.brisbaneDay}</strong> {b.brisbaneDate} · <strong>{b.brisbaneTime}</strong> ·{' '}
      {b.locationName}
      {b.customerName ? (
        <>
          {' '}
          · {b.customerName}
          {b.customerPhone ? ` (${b.customerPhone})` : ''}
        </>
      ) : null}
      {b.serviceNames.length > 0 && (
        <span style={{ color: 'var(--color-text-muted)' }}> · {b.serviceNames.join(', ')}</span>
      )}
    </li>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--spacing-6)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 720,
          backgroundColor: 'var(--color-surface, #fff)',
          borderRadius: 'var(--radius-lg, 8px)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--spacing-4) var(--spacing-5)',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>{title}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: '1px solid var(--color-border)',
              padding: '4px 10px',
              borderRadius: 'var(--radius-default, 4px)',
              cursor: 'pointer',
              color: 'var(--color-text)',
            }}
          >
            Close
          </button>
        </div>
        <div style={{ padding: 'var(--spacing-5)', overflowY: 'auto', flex: 1 }}>{children}</div>
      </div>
    </div>
  );
}

function DeactivationDialog({
  barber,
  onCancel,
  onConfirmed,
}: {
  barber: any;
  onCancel: () => void;
  onConfirmed: (impact: Impact) => void;
}) {
  const queryClient = useQueryClient();
  const {
    data: impact,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['deactivation-impact', barber.id],
    queryFn: () => api.getBarberDeactivationImpact(barber.id),
    staleTime: 0,
  });

  const deactivateMutation = useMutation({
    mutationFn: () => api.deleteBarber(barber.id),
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ['barbers'] });
      queryClient.invalidateQueries({ queryKey: ['rosters'] });
      queryClient.invalidateQueries({ queryKey: ['breaks'] });
      onConfirmed(result?.impact ?? impact);
    },
  });

  return (
    <ModalShell title={`Deactivate ${barber.name}?`} onClose={onCancel}>
      {isLoading && <p>Checking impact…</p>}
      {error && (
        <p style={{ color: 'var(--color-error, #dc3545)' }}>
          Couldn't load impact preview: {(error as any)?.message ?? 'unknown error'}
        </p>
      )}
      {impact && (
        <>
          <p style={{ marginTop: 0 }}>
            <strong>{barber.name}</strong> will be marked inactive and removed from all future
            booking flows.
          </p>
          <ul style={{ marginBottom: 'var(--spacing-4)' }}>
            <li>
              <strong>{impact.shiftsToRemove}</strong> future rostered shift
              {impact.shiftsToRemove === 1 ? '' : 's'} will be deleted.
            </li>
            <li>
              <strong>{impact.breaksToRemove}</strong> future break
              {impact.breaksToRemove === 1 ? '' : 's'} will be deleted.
            </li>
            <li>
              <strong>{impact.affectedBookings.length}</strong> future booking
              {impact.affectedBookings.length === 1 ? ' is' : 's are'} still on the calendar and
              will need to be reassigned or cancelled manually.
            </li>
          </ul>

          {impact.affectedBookings.length > 0 ? (
            <>
              <h3 style={{ fontSize: 'var(--font-size-base)', margin: '0 0 var(--spacing-2)' }}>
                Bookings needing attention:
              </h3>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 20,
                  maxHeight: 300,
                  overflowY: 'auto',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {impact.affectedBookings.map((b: ImpactBooking) => (
                  <BookingRow key={b.bookingId} b={b} />
                ))}
              </ul>
            </>
          ) : (
            <p style={{ color: 'var(--color-text-muted)' }}>No future bookings to handle. ✓</p>
          )}

          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-2)',
              justifyContent: 'flex-end',
              marginTop: 'var(--spacing-4)',
            }}
          >
            <button
              onClick={onCancel}
              style={{
                padding: '8px 14px',
                border: '1px solid var(--color-border)',
                background: 'var(--color-surface)',
                borderRadius: 'var(--radius-default, 4px)',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => deactivateMutation.mutate()}
              disabled={deactivateMutation.isPending}
              style={{
                padding: '8px 14px',
                border: '1px solid var(--color-error, #dc3545)',
                background: 'var(--color-error, #dc3545)',
                color: '#fff',
                borderRadius: 'var(--radius-default, 4px)',
                cursor: deactivateMutation.isPending ? 'wait' : 'pointer',
                fontWeight: 600,
              }}
            >
              {deactivateMutation.isPending ? 'Deactivating…' : 'Deactivate'}
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

function PostDeactivationAlert({ impact, onClose }: { impact: Impact; onClose: () => void }) {
  return (
    <ModalShell
      title={`${impact.barberName} deactivated — ${impact.affectedBookings.length} booking${impact.affectedBookings.length === 1 ? '' : 's'} need${impact.affectedBookings.length === 1 ? 's' : ''} attention`}
      onClose={onClose}
    >
      <p style={{ marginTop: 0 }}>
        Future shifts and breaks for <strong>{impact.barberName}</strong> have been removed. The
        following bookings are still on the calendar and need to be manually reassigned or
        cancelled:
      </p>
      <ul
        style={{
          margin: 0,
          paddingLeft: 20,
          maxHeight: 400,
          overflowY: 'auto',
          fontSize: 'var(--font-size-sm)',
        }}
      >
        {impact.affectedBookings.map((b: ImpactBooking) => (
          <BookingRow key={b.bookingId} b={b} />
        ))}
      </ul>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 'var(--spacing-4)',
        }}
      >
        <button
          onClick={onClose}
          style={{
            padding: '8px 14px',
            border: '1px solid var(--color-accent)',
            background: 'var(--color-accent)',
            color: 'var(--color-surface)',
            borderRadius: 'var(--radius-default, 4px)',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          I'll handle these
        </button>
      </div>
    </ModalShell>
  );
}
